/**
 * Incident Investigation Page
 * 
 * Context-aware investigation hub for:
 * - Entity Investigation (Users, Devices, IPs, Emails)
 * - Advanced Hunting (KQL Queries)
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  User,
  Monitor,
  AlertTriangle,
  Loader2,
  Target,
  Globe,
  Mail,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { UserInvestigationCard } from '../components/investigation/UserInvestigationCard';
import { DeviceInvestigationCard } from '../components/investigation/DeviceInvestigationCard';
import { SkeletonCard } from '../components/ui/Skeleton';
import {
  investigateUser,
  investigateDevice,
  runEntityHuntingQuery,
  getHuntingPresets,
} from '../services/investigation';
import { getIncidentDetail } from '../services';
import { useIncidentAssistantSafe } from '../context/IncidentAssistantContext';
import { cn } from '../lib/utils';
import { HuntingResultsView } from '../components/investigation/HuntingResultsView';
import type {
  UserInvestigationResult,
  DeviceInvestigationResult,
  HuntingQueryResult,
  HuntingPresetsResponse,
  Entity,
} from '../data/types';

// Entity types for investigation
type InvestigateEntityType = 'user' | 'device' | 'ip' | 'email';

interface InvestigateEntity {
  type: InvestigateEntityType;
  name: string;
  value: string;
}

export function IncidentInvestigation() {
  const { tenantId, incidentId } = useParams<{ tenantId: string; incidentId: string }>();
  const navigate = useNavigate();
  
  // Incident data
  const [tenantDisplayName, setTenantDisplayName] = useState<string | undefined>();
  const [entities, setEntities] = useState<Entity[]>([]);
  const [isLoadingIncident, setIsLoadingIncident] = useState(true);

  // Selected entity
  const [selectedEntity, setSelectedEntity] = useState<InvestigateEntity | null>(null);
  
  // Entity investigation state
  const [isLoadingEntity, setIsLoadingEntity] = useState(false);
  const [entityError, setEntityError] = useState<string | null>(null);
  const [userResult, setUserResult] = useState<UserInvestigationResult | null>(null);
  const [deviceResult, setDeviceResult] = useState<DeviceInvestigationResult | null>(null);

  // Hunting state
  const [huntingPresets, setHuntingPresets] = useState<HuntingPresetsResponse | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [huntingResult, setHuntingResult] = useState<HuntingQueryResult | null>(null);
  const [isLoadingHunting, setIsLoadingHunting] = useState(false);
  const [huntingError, setHuntingError] = useState<string | null>(null);
  
  // Cache
  const [huntingCache, setHuntingCache] = useState<Map<string, HuntingQueryResult>>(new Map());
  const [userCache, setUserCache] = useState<Map<string, UserInvestigationResult>>(new Map());
  const [deviceCache, setDeviceCache] = useState<Map<string, DeviceInvestigationResult>>(new Map());

  const assistantContext = useIncidentAssistantSafe();

  // Fetch incident data
  useEffect(() => {
    async function fetchIncidentData() {
      if (!tenantId || !incidentId) return;
      
      setIsLoadingIncident(true);
      try {
        const result = await getIncidentDetail(tenantId, incidentId);
        if (result.success && result.data) {
          setTenantDisplayName(result.data.incident.tenantDisplayName);
          setEntities(result.data.entities);
        }
      } catch (err) {
        console.error('Error fetching incident data:', err);
      } finally {
        setIsLoadingIncident(false);
      }
    }
    
    fetchIncidentData();
  }, [tenantId, incidentId]);

  // Fetch hunting presets on mount
  useEffect(() => {
    async function fetchPresets() {
      if (tenantId) {
        const result = await getHuntingPresets(tenantId);
        if (result.success) {
          setHuntingPresets(result.data);
        }
      }
    }
    fetchPresets();
  }, [tenantId]);

  // Extract entities for investigation
  const investigateEntities = useMemo(() => {
    const result: InvestigateEntity[] = [];
    
    entities.forEach(e => {
      const value = e.value || (e.details as Record<string, unknown>)?.userPrincipalName as string 
        || (e.details as Record<string, unknown>)?.upn as string
        || (e.details as Record<string, unknown>)?.deviceId as string
        || (e.details as Record<string, unknown>)?.id as string
        || e.name;
      
      if (e.type === 'user') {
        result.push({ type: 'user', name: e.name, value });
        if (value.includes('@')) {
          result.push({ type: 'email', name: e.name, value });
        }
      } else if (e.type === 'device') {
        result.push({ type: 'device', name: e.name, value });
      } else if (e.type === 'ip') {
        result.push({ type: 'ip', name: e.name, value });
      }
    });
    
    return result;
  }, [entities]);

  // Group entities by type
  const entityGroups = useMemo(() => {
    const groups: Record<InvestigateEntityType, InvestigateEntity[]> = {
      user: [],
      device: [],
      ip: [],
      email: [],
    };
    investigateEntities.forEach(e => {
      groups[e.type].push(e);
    });
    return groups;
  }, [investigateEntities]);

  // Get available presets for selected entity type
  const availablePresets = useMemo(() => {
    if (!selectedEntity || !huntingPresets) return [];
    return huntingPresets.entityPresets[selectedEntity.type] || [];
  }, [selectedEntity, huntingPresets]);

  // Handle entity selection
  const handleSelectEntity = useCallback(async (entity: InvestigateEntity) => {
    setSelectedEntity(entity);
    setSelectedPreset(null);
    setHuntingResult(null);
    setHuntingError(null);
    setEntityError(null);
    setUserResult(null);
    setDeviceResult(null);

    // For user/device, fetch Graph data
    if (entity.type === 'user') {
      if (userCache.has(entity.value)) {
        setUserResult(userCache.get(entity.value)!);
        return;
      }
      
      setIsLoadingEntity(true);
      try {
        const result = await investigateUser(tenantId!, entity.value);
        if (result.success) {
          setUserResult(result.data);
          setUserCache(prev => new Map(prev).set(entity.value, result.data));
          assistantContext?.addUserInvestigation(entity.value, result.data);
        } else {
          setEntityError(result.error || 'Failed to investigate user');
        }
      } catch (err) {
        setEntityError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoadingEntity(false);
      }
    } else if (entity.type === 'device') {
      if (deviceCache.has(entity.value)) {
        setDeviceResult(deviceCache.get(entity.value)!);
        return;
      }
      
      setIsLoadingEntity(true);
      try {
        const result = await investigateDevice(tenantId!, entity.value);
        if (result.success) {
          setDeviceResult(result.data);
          setDeviceCache(prev => new Map(prev).set(entity.value, result.data));
          assistantContext?.addDeviceInvestigation(entity.value, result.data);
        } else {
          setEntityError(result.error || 'Failed to investigate device');
      }
    } catch (err) {
        setEntityError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
        setIsLoadingEntity(false);
      }
    }
  }, [tenantId, userCache, deviceCache, assistantContext]);

  // Run hunting query
  const handleRunHunting = useCallback(async (presetId: string) => {
    if (!selectedEntity || !tenantId) return;
    
    const cacheKey = `${selectedEntity.type}:${selectedEntity.value}:${presetId}`;
    
    if (huntingCache.has(cacheKey)) {
      setHuntingResult(huntingCache.get(cacheKey)!);
      setSelectedPreset(presetId);
      return;
    }
    
    setSelectedPreset(presetId);
    setIsLoadingHunting(true);
    setHuntingError(null);

      try {
      const result = await runEntityHuntingQuery(tenantId, presetId, selectedEntity.type, selectedEntity.value);
      
          if (result.success) {
        setHuntingResult(result.data);
        setHuntingCache(prev => new Map(prev).set(cacheKey, result.data));
          } else {
        setHuntingError(result.error || 'Query failed');
        }
      } catch (err) {
      setHuntingError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
      setIsLoadingHunting(false);
      }
  }, [tenantId, selectedEntity, huntingCache]);

  if (!tenantId || !incidentId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-text-primary mb-2">Invalid Parameters</h2>
          <p className="text-text-secondary mb-4">Missing tenant or incident information.</p>
          <Button onClick={() => navigate('/incidents')}>
              <ArrowLeft className="w-4 h-4" />
              Back to Incidents
            </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back navigation */}
      <button
        onClick={() => navigate(`/incidents/${tenantId}/${incidentId}`)}
        className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back to Incident #{incidentId}</span>
      </button>

      {/* Page Header */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-accent-blue" />
            Investigation Hub
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary mb-2">
            Select an entity from this incident to investigate. You'll see Graph API data and can run Advanced Hunting queries.
          </p>
        {tenantDisplayName && (
            <p className="text-xs text-text-secondary">Tenant: {tenantDisplayName}</p>
        )}
        </CardContent>
      </Card>

      {isLoadingIncident ? (
        <SkeletonCard />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left: Entity Selection */}
          <div className="space-y-4">
            {entityGroups.user.length > 0 && (
          <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-accent-blue" />
                    Users ({entityGroups.user.length})
                  </CardTitle>
            </CardHeader>
                <CardContent className="space-y-1">
                  {entityGroups.user.map((entity, idx) => (
                <button
                      key={idx}
                      onClick={() => handleSelectEntity(entity)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                        selectedEntity?.value === entity.value && selectedEntity?.type === entity.type
                          ? "bg-accent-blue/15 border border-blue-300 dark:border-blue-700"
                          : "bg-bg-tertiary border border-border-default hover:border-blue-300"
                      )}
                    >
                      <div className="font-medium text-text-primary truncate">{entity.name}</div>
                      <div className="text-xs text-text-secondary truncate">{entity.value}</div>
                </button>
                  ))}
                </CardContent>
              </Card>
            )}

            {entityGroups.email.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-purple-500" />
                    Email Addresses ({entityGroups.email.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {entityGroups.email.map((entity, idx) => (
                <button
                      key={idx}
                      onClick={() => handleSelectEntity(entity)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                        selectedEntity?.value === entity.value && selectedEntity?.type === entity.type
                          ? "bg-purple-100 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-700"
                          : "bg-bg-tertiary border border-border-default hover:border-purple-300"
                      )}
                >
                      <div className="text-xs text-text-secondary truncate">{entity.value}</div>
                </button>
                  ))}
            </CardContent>
          </Card>
            )}

            {entityGroups.device.length > 0 && (
            <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Monitor className="w-4 h-4 text-green-500" />
                    Devices ({entityGroups.device.length})
                  </CardTitle>
              </CardHeader>
                <CardContent className="space-y-1">
                  {entityGroups.device.map((entity, idx) => (
                        <button
                      key={idx}
                      onClick={() => handleSelectEntity(entity)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                        selectedEntity?.value === entity.value && selectedEntity?.type === entity.type
                          ? "bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700"
                          : "bg-bg-tertiary border border-border-default hover:border-green-300"
                      )}
                        >
                      <div className="font-medium text-text-primary truncate">{entity.name}</div>
                        </button>
                      ))}
                </CardContent>
              </Card>
                )}

            {entityGroups.ip.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Globe className="w-4 h-4 text-orange-500" />
                    IP Addresses ({entityGroups.ip.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {entityGroups.ip.map((entity, idx) => (
                        <button
                      key={idx}
                      onClick={() => handleSelectEntity(entity)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                        selectedEntity?.value === entity.value && selectedEntity?.type === entity.type
                          ? "bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700"
                          : "bg-bg-tertiary border border-border-default hover:border-orange-300"
                      )}
                        >
                      <div className="font-mono text-text-primary">{entity.value}</div>
                        </button>
                      ))}
                </CardContent>
              </Card>
                )}

            {investigateEntities.length === 0 && (
              <Card>
                <CardContent className="p-6 text-center">
                  <Search className="w-10 h-10 text-text-muted mx-auto mb-3" />
                  <p className="text-sm text-text-secondary">No entities found in this incident</p>
              </CardContent>
            </Card>
          )}
        </div>

          {/* Right: Investigation Panel */}
          <div className="lg:col-span-3 space-y-6">
            {!selectedEntity ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <ChevronRight className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">Select an Entity</h3>
                  <p className="text-text-secondary max-w-md mx-auto">
                    Choose a user, device, IP, or email address from the left panel to start investigating.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Selected Entity Header */}
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-accent-blue/15">
                        {selectedEntity.type === 'user' && <User className="w-5 h-5 text-accent-blue" />}
                        {selectedEntity.type === 'device' && <Monitor className="w-5 h-5 text-green-600" />}
                        {selectedEntity.type === 'ip' && <Globe className="w-5 h-5 text-orange-600" />}
                        {selectedEntity.type === 'email' && <Mail className="w-5 h-5 text-purple-600" />}
                      </div>
                      <div>
                        <p className="text-xs text-text-secondary uppercase">{selectedEntity.type}</p>
                        <p className="font-semibold text-text-primary">{selectedEntity.name || selectedEntity.value}</p>
                        {selectedEntity.name && selectedEntity.name !== selectedEntity.value && (
                          <p className="text-sm text-text-secondary font-mono">{selectedEntity.value}</p>
                        )}
              </div>
            </div>
                  </CardContent>
                </Card>

                {/* Graph API Data */}
                {(selectedEntity.type === 'user' || selectedEntity.type === 'device') && (
                  <>
                    {isLoadingEntity && (
                      <Card>
                        <CardContent className="p-8 text-center">
                          <Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto mb-3" />
                          <p className="text-text-secondary">Loading {selectedEntity.type} data...</p>
                        </CardContent>
                      </Card>
          )}

                    {entityError && (
            <Card>
                        <CardContent className="p-6">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-text-primary">Could not load Graph API data</p>
                              <p className="text-sm text-text-secondary mt-1">{entityError}</p>
                            </div>
                </div>
              </CardContent>
            </Card>
          )}

                    {userResult && <UserInvestigationCard result={userResult} />}
                    {deviceResult && <DeviceInvestigationCard result={deviceResult} />}
                  </>
          )}

                {/* Advanced Hunting */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Target className="w-5 h-5 text-accent-blue" />
                      Advanced Hunting
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-text-secondary">
                      Run KQL queries against 30 days of Defender data for <strong>{selectedEntity.value}</strong>
                    </p>

                    {availablePresets.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {availablePresets.map((preset) => {
                          const isUnavailable = preset.available === false;
                          const disabled = isLoadingHunting || isUnavailable;
                          return (
                            <button
                              key={preset.id}
                              onClick={() => handleRunHunting(preset.id)}
                              disabled={disabled}
                              title={
                                isUnavailable
                                  ? preset.unavailableReason ?? 'This preset is not available for this tenant'
                                  : undefined
                              }
                              className={cn(
                                "text-left p-3 rounded-lg border transition-colors",
                                isUnavailable
                                  ? "bg-bg-tertiary/40 border-border-default opacity-50 cursor-not-allowed"
                                  : selectedPreset === preset.id
                                    ? "bg-accent-blue/10 border-blue-300 dark:border-blue-700"
                                    : "bg-bg-tertiary border-border-default hover:border-blue-300",
                                isLoadingHunting && selectedPreset === preset.id && "animate-pulse"
                              )}
                            >
                              <p className="font-medium text-sm text-text-primary">{preset.name}</p>
                              <p className="text-xs text-text-secondary mt-1 line-clamp-2">{preset.description}</p>
                              {isUnavailable && preset.unavailableReason && (
                                <p className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400 mt-2">
                                  {preset.unavailableReason}
                                </p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-text-secondary">No hunting queries available for {selectedEntity.type} entities.</p>
                    )}

                    {isLoadingHunting && (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
                      </div>
                    )}

                    {huntingError && !isLoadingHunting && (
                      <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-red-800 dark:text-red-400">Query Failed</p>
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{huntingError}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {huntingResult && !isLoadingHunting && (
                      <HuntingResultsView result={huntingResult} />
                    )}
              </CardContent>
            </Card>
              </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
